import { useState } from "react";
import SectionLabel from "@/components/SectionLabel";
import { CLINIC, waLink, telLink } from "@/lib/clinic";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail, MapPin, Clock, CheckCircle2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill required fields."); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent!");
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setLoading(false); }
  };

  return (
    <div>
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-10">
          <div>
            <SectionLabel>Contact</SectionLabel>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-slate-900">Visit, call or write to us.</h1>
            <p className="mt-3 text-slate-600 max-w-md">We'd be happy to hear from you — booking, feedback, or questions.</p>

            <div className="mt-8 space-y-4 text-slate-700">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#0A6EBD] mt-0.5" />
                <div>
                  <div className="font-semibold text-slate-900">Address</div>
                  <div className="text-sm">{CLINIC.address.line1}<br />{CLINIC.address.line2}<br />{CLINIC.address.city} – {CLINIC.address.pin}, {CLINIC.address.state}</div>
                </div>
              </div>
              <a href={telLink()} className="flex items-start gap-3 hover:text-[#0A6EBD]" data-testid="contact-phone">
                <Phone className="h-5 w-5 text-[#0A6EBD] mt-0.5" />
                <div><div className="font-semibold text-slate-900">Phone</div><div className="text-sm">{CLINIC.phoneDisplay}</div></div>
              </a>
              <a href={waLink("Hello Aarogya Clinic")} target="_blank" rel="noreferrer" className="flex items-start gap-3 hover:text-[#0A6EBD]" data-testid="contact-whatsapp">
                <MessageCircle className="h-5 w-5 text-[#25D366] mt-0.5" />
                <div><div className="font-semibold text-slate-900">WhatsApp</div><div className="text-sm">Quick chat with our team</div></div>
              </a>
              <a href={`mailto:${CLINIC.email}`} className="flex items-start gap-3 hover:text-[#0A6EBD]" data-testid="contact-email">
                <Mail className="h-5 w-5 text-[#0A6EBD] mt-0.5" />
                <div><div className="font-semibold text-slate-900">Email</div><div className="text-sm">{CLINIC.email}</div></div>
              </a>
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-[#0A6EBD] mt-0.5" />
                <div>
                  <div className="font-semibold text-slate-900">Working Hours</div>
                  {CLINIC.hours.map((h, i) => (
                    <div key={i} className="text-sm text-slate-600">{h.day}: {h.evening}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div>
            {done ? (
              <div className="rounded-3xl border border-slate-200/70 p-8 aarogya-shadow bg-white text-center">
                <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
                <h3 className="mt-4 font-serif text-2xl text-slate-900">Message received</h3>
                <p className="text-slate-600 mt-2">Thanks {form.name}. We'll respond within one business day.</p>
                <button onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", subject: "", message: "" }); }}
                        className="mt-4 rounded-full px-5 py-2.5 text-sm font-semibold text-white" style={{ background: "#0A6EBD" }}>
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl border border-slate-200/70 p-6 md:p-8 bg-white aarogya-shadow space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name *"><input required value={form.name} onChange={e=>update("name", e.target.value)} className={inp()} data-testid="contact-name" /></Field>
                  <Field label="Email *"><input required type="email" value={form.email} onChange={e=>update("email", e.target.value)} className={inp()} data-testid="contact-email-input" /></Field>
                  <Field label="Phone"><input value={form.phone} onChange={e=>update("phone", e.target.value)} className={inp()} data-testid="contact-phone-input" /></Field>
                  <Field label="Subject"><input value={form.subject} onChange={e=>update("subject", e.target.value)} className={inp()} data-testid="contact-subject" /></Field>
                </div>
                <Field label="Message *">
                  <textarea required rows={5} value={form.message} onChange={e=>update("message", e.target.value)} className={inp()} data-testid="contact-message" />
                </Field>
                <button disabled={loading} className="w-full rounded-full px-6 py-3 text-white font-semibold aarogya-shadow disabled:opacity-70"
                        style={{ background: "#0A6EBD" }} data-testid="contact-submit-btn">
                  {loading ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="rounded-3xl overflow-hidden aarogya-shadow border border-slate-200/70">
            <iframe
              title="Aarogya Clinic Location"
              src={`https://www.google.com/maps?q=${encodeURIComponent(CLINIC.mapsQuery)}&output=embed`}
              width="100%" height="380" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

const inp = () => "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A6EBD] focus:border-transparent";
function Field({ label, children }) {
  return <label className="block"><div className="text-xs font-semibold text-slate-600 mb-1.5">{label}</div>{children}</label>;
}
